"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { CheckCircle2, ChevronDown, Loader2, Send } from "lucide-react";
import { PHONE_DISPLAY, type LandingReferrer } from "./landing-shared";

/* ─────────────────────────────────────────────────────────────
 * Inline-Anfrageformular
 *
 * Bewusst on-page (kein Absprung zu /anfrage): Empfänger einer
 * Mail-Akquise sollen im Funnel bleiben und in unter einer Minute
 * absenden können. Absenden geht an /api/anfrage → speichert DB-first
 * in contact_submissions (taucht in /admin/kontaktanfragen auf, geht
 * auch bei SMTP-Ausfall nicht verloren) + verschickt Mail. Das
 * `source`-Feld markiert den Lead als von dieser Landingpage kommend.
 *
 * Drei Pflichtfelder: Name, Telefon oder E-Mail, Worum geht es. Firma
 * und Freitext sind optional und eingeklappt. Keine Einwilligungs-
 * Checkbox – bei Kontaktanfragen reicht der Hinweis auf die
 * Datenschutzerklärung (Art. 6 Abs. 1 lit. b DSGVO).
 * ──────────────────────────────────────────────────────────── */

const LANDING_SERVICES = [
  "Website",
  "Software / eigene App",
  "Marketing (Google & Social Media)",
  "Mehrere Bereiche",
  "Bin mir noch unsicher",
];

/** Quelle aus dem URL-Parameter (?via=pcboot) – wird als Lead-Herkunft mitgeschickt. */
function readQuelle(fallback: LandingReferrer): string {
  if (typeof window === "undefined") return fallback ?? "";
  const via = new URLSearchParams(window.location.search).get("via");
  return (via ?? fallback ?? "").toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 40);
}

export function LandingInquiryForm({ referrer, source }: { referrer: LandingReferrer; source?: string }) {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState<{ viaEmail: boolean } | null>(null);
  const [error, setError] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [quelle, setQuelle] = useState<string>(referrer ?? "");

  useEffect(() => {
    setQuelle(readQuelle(referrer));
  }, [referrer]);

  const inputStyle = {
    backgroundColor: "var(--bg)",
    borderColor: "var(--border)",
    color: "var(--text)",
  } as const;
  const inputClass =
    "w-full rounded-lg border px-4 py-3 text-base outline-none transition-colors focus:border-brand-500";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const form = e.currentTarget;
    const val = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null)?.value?.trim() ?? "";

    // Ein Feld für Telefon ODER E-Mail: enthält es ein @, ist es die Mail.
    const contact = val("contact");
    const isEmail = contact.includes("@");

    const data = {
      service: val("service"),
      name: val("name"),
      company: val("company"),
      email: isEmail ? contact : "",
      phone: isEmail ? "" : contact,
      description: val("description"),
      website: val("website"), // Honeypot
      quelle,
      // Keine Checkbox: Verarbeitung zur Anfragebearbeitung, Hinweis steht unter dem Button.
      consent: false,
      source: source ?? (quelle ? `Einführungs-Landingpage (quelle=${quelle})` : "Einführungs-Landingpage (direkt)"),
    };

    try {
      const res = await fetch("/api/anfrage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || "Anfrage konnte nicht gesendet werden.");
      }
      setSubmitted({ viaEmail: isEmail });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="rounded-2xl border p-8 text-center sm:p-10"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
      >
        <CheckCircle2 className="mx-auto h-14 w-14 text-brand-500" />
        <h3 className="mt-4 text-2xl font-semibold" style={{ color: "var(--text)" }}>
          Anfrage erhalten – danke!
        </h3>
        <p className="mx-auto mt-3 max-w-md text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
          {submitted.viaEmail
            ? "Wir melden uns am selben Werktag persönlich bei Ihnen. Eine kurze Bestätigung ist unterwegs."
            : `Wir rufen Sie am selben Werktag zurück. Wenn es eilt: ${PHONE_DISPLAY}.`}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border p-6 sm:p-8"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", boxShadow: "var(--card-shadow-md)" }}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="lp-name" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
            Ihr Name *
          </label>
          <input
            type="text"
            id="lp-name"
            name="name"
            required
            minLength={2}
            autoComplete="name"
            maxLength={80}
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div>
          <label htmlFor="lp-contact" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
            Telefon oder E-Mail *
          </label>
          <input
            type="text"
            id="lp-contact"
            name="contact"
            required
            minLength={5}
            maxLength={120}
            placeholder="z. B. 09771 12345 oder name@firma.de"
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div>
          <label htmlFor="lp-service" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
            Worum geht es? (optional)
          </label>
          <select
            id="lp-service"
            name="service"
            defaultValue=""
            className={`${inputClass} cursor-pointer`}
            style={inputStyle}
          >
            <option value="">Noch nicht sicher – wir klären es im Gespräch</option>
            {LANDING_SERVICES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Optionales, eingeklappt – jedes sichtbare Feld kostet Anfragen. */}
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          aria-expanded={showMore}
          className="inline-flex items-center gap-1.5 text-sm font-medium"
          style={{ color: "var(--text-tertiary)" }}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${showMore ? "rotate-180" : ""}`} />
          {showMore ? "Weniger Angaben" : "Mehr Angaben (optional)"}
        </button>
        <div className={showMore ? "space-y-4" : "hidden"}>
          <div>
            <label htmlFor="lp-company" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
              Unternehmen
            </label>
            <input
              type="text"
              id="lp-company"
              name="company"
              autoComplete="organization"
              maxLength={100}
              className={inputClass}
              style={inputStyle}
            />
          </div>
          <div>
            <label htmlFor="lp-description" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
              Worum geht es genau?
            </label>
            <textarea
              id="lp-description"
              name="description"
              rows={3}
              maxLength={1500}
              placeholder="Ein, zwei Sätze genügen – den Rest klären wir am Telefon."
              className={`${inputClass} resize-none`}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Honeypot – für Menschen unsichtbar */}
        <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        {/* Herkunft aus dem URL-Parameter (?via=pcboot) */}
        <input type="hidden" name="quelle" value={quelle} readOnly />
      </div>

      {error && (
        <p className="mt-4 text-sm font-medium" style={{ color: "var(--fi-error-fg)" }} role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-6 py-3.5 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Wird gesendet …
          </>
        ) : (
          <>
            <Send className="h-5 w-5" />
            Anfrage senden – wir melden uns heute noch.
          </>
        )}
      </button>
      <p className="mt-3 text-center text-xs leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
        Mit dem Absenden verarbeiten wir Ihre Angaben zur Bearbeitung der Anfrage, Details in der{" "}
        <Link href="/datenschutz" className="underline hover:text-brand-500">
          Datenschutzerklärung
        </Link>
        . Kein Newsletter, kein Spam.
      </p>
    </form>
  );
}
